/**
 * usePokemon Hook - Data fetching and caching for PokeAPI
 * Handles all Pokemon-related API calls with caching
 */
import { useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const POKEAPI_BASE = 'https://pokeapi.co/api/v2';

// Generation ranges
export const GENERATIONS = {
  1: { start: 1, end: 151, name: 'Kanto' },
  2: { start: 152, end: 251, name: 'Johto' },
  3: { start: 252, end: 386, name: 'Hoenn' },
  4: { start: 387, end: 493, name: 'Sinnoh' },
  5: { start: 494, end: 649, name: 'Unova' },
  6: { start: 650, end: 721, name: 'Kalos' },
  7: { start: 722, end: 809, name: 'Alola' },
  8: { start: 810, end: 905, name: 'Galar' },
  9: { start: 906, end: 1025, name: 'Paldea' }
};

// Type effectiveness chart
export const TYPE_CHART = {
  normal: { weakTo: ['fighting'], resistantTo: [], immuneTo: ['ghost'] },
  fire: { weakTo: ['water', 'ground', 'rock'], resistantTo: ['fire', 'grass', 'ice', 'bug', 'steel', 'fairy'], immuneTo: [] },
  water: { weakTo: ['electric', 'grass'], resistantTo: ['fire', 'water', 'ice', 'steel'], immuneTo: [] },
  electric: { weakTo: ['ground'], resistantTo: ['electric', 'flying', 'steel'], immuneTo: [] },
  grass: { weakTo: ['fire', 'ice', 'poison', 'flying', 'bug'], resistantTo: ['water', 'electric', 'grass', 'ground'], immuneTo: [] },
  ice: { weakTo: ['fire', 'fighting', 'rock', 'steel'], resistantTo: ['ice'], immuneTo: [] },
  fighting: { weakTo: ['flying', 'psychic', 'fairy'], resistantTo: ['bug', 'rock', 'dark'], immuneTo: [] },
  poison: { weakTo: ['ground', 'psychic'], resistantTo: ['grass', 'fighting', 'poison', 'bug', 'fairy'], immuneTo: [] },
  ground: { weakTo: ['water', 'grass', 'ice'], resistantTo: ['poison', 'rock'], immuneTo: ['electric'] },
  flying: { weakTo: ['electric', 'ice', 'rock'], resistantTo: ['grass', 'fighting', 'bug'], immuneTo: ['ground'] },
  psychic: { weakTo: ['bug', 'ghost', 'dark'], resistantTo: ['fighting', 'psychic'], immuneTo: [] },
  bug: { weakTo: ['fire', 'flying', 'rock'], resistantTo: ['grass', 'fighting', 'ground'], immuneTo: [] },
  rock: { weakTo: ['water', 'grass', 'fighting', 'ground', 'steel'], resistantTo: ['normal', 'fire', 'poison', 'flying'], immuneTo: [] },
  ghost: { weakTo: ['ghost', 'dark'], resistantTo: ['poison', 'bug'], immuneTo: ['normal', 'fighting'] },
  dragon: { weakTo: ['ice', 'dragon', 'fairy'], resistantTo: ['fire', 'water', 'electric', 'grass'], immuneTo: [] },
  dark: { weakTo: ['fighting', 'bug', 'fairy'], resistantTo: ['ghost', 'dark'], immuneTo: ['psychic'] },
  steel: { weakTo: ['fire', 'fighting', 'ground'], resistantTo: ['normal', 'grass', 'ice', 'flying', 'psychic', 'bug', 'rock', 'dragon', 'steel', 'fairy'], immuneTo: ['poison'] },
  fairy: { weakTo: ['poison', 'steel'], resistantTo: ['fighting', 'bug', 'dark'], immuneTo: ['dragon'] }
};

export function usePokemon() {
  const { addToCache, getFromCache, language } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch with caching
  const fetchWithCache = useCallback(async (url, cacheKey) => {
    const cached = getFromCache(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch: ${url}`);
    
    const data = await response.json();
    addToCache(cacheKey, data);
    return data;
  }, [addToCache, getFromCache]);
  
  // Get basic pokemon list (all 1025)
  const getPokemonList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/pokemon?limit=1025`,
        'pokemon-list'
      );
      return data.results.map((p, index) => ({
        id: index + 1,
        name: p.name,
        url: p.url
      }));
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchWithCache]);
  
  // Get pokemon details
  const getPokemon = useCallback(async (idOrName) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/pokemon/${idOrName}`,
        `pokemon-${idOrName}`
      );
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWithCache]);
  
  // Get pokemon species (for description, generation, etc.)
  const getPokemonSpecies = useCallback(async (idOrName) => {
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/pokemon-species/${idOrName}`,
        `species-${idOrName}`
      );
      return data;
    } catch (err) {
      return null;
    }
  }, [fetchWithCache]);
  
  // Get ability details
  const getAbility = useCallback(async (name) => {
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/ability/${name}`,
        `ability-${name}`
      );
      return data;
    } catch (err) {
      return null;
    }
  }, [fetchWithCache]);
  
  // Get move details
  const getMove = useCallback(async (name) => {
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/move/${name}`,
        `move-${name}`
      );
      return data;
    } catch (err) {
      return null;
    }
  }, [fetchWithCache]);
  
  // Get evolution chain
  const getEvolutionChain = useCallback(async (url) => {
    try {
      const id = url.split('/').filter(Boolean).pop();
      const data = await fetchWithCache(url, `evolution-${id}`);
      return data;
    } catch (err) {
      return null;
    }
  }, [fetchWithCache]);
  
  // Get type details
  const getType = useCallback(async (name) => {
    try {
      const data = await fetchWithCache(
        `${POKEAPI_BASE}/type/${name}`,
        `type-${name}`
      );
      return data;
    } catch (err) {
      return null;
    }
  }, [fetchWithCache]);
  
  // Get localized name from entries
  const getLocalizedName = useCallback((names) => {
    const langCode = language === 'es' ? 'es' : 'en';
    const localized = names?.find(n => n.language.name === langCode);
    return localized?.name || names?.[0]?.name || '';
  }, [language]);
  
  // Get localized flavor text
  const getLocalizedFlavorText = useCallback((flavorTexts) => {
    const langCode = language === 'es' ? 'es' : 'en';
    const localized = flavorTexts?.find(f => f.language.name === langCode);
    return localized?.flavor_text?.replace(/\f/g, ' ')?.replace(/\n/g, ' ') || '';
  }, [language]);
  
  // Get localized effect text
  const getLocalizedEffect = useCallback((effectEntries) => {
    const langCode = language === 'es' ? 'es' : 'en';
    let localized = effectEntries?.find(e => e.language.name === langCode);
    if (!localized) {
      localized = effectEntries?.find(e => e.language.name === 'en');
    }
    return localized?.short_effect || localized?.effect || '';
  }, [language]);
  
  // Calculate type effectiveness
  const calculateTypeEffectiveness = useCallback((types) => {
    const weaknesses = new Set();
    const resistances = new Set();
    const immunities = new Set();
    
    types.forEach(type => {
      const typeData = TYPE_CHART[type];
      if (typeData) {
        typeData.weakTo.forEach(t => weaknesses.add(t));
        typeData.resistantTo.forEach(t => resistances.add(t));
        typeData.immuneTo.forEach(t => immunities.add(t));
      }
    });
    
    // Remove immunities from weaknesses and resistances
    immunities.forEach(t => {
      weaknesses.delete(t);
      resistances.delete(t);
    });
    
    // If weak and resistant to same type, they cancel
    weaknesses.forEach(t => {
      if (resistances.has(t)) {
        weaknesses.delete(t);
        resistances.delete(t);
      }
    });
    
    return {
      weaknesses: Array.from(weaknesses),
      resistances: Array.from(resistances),
      immunities: Array.from(immunities)
    };
  }, []);
  
  // Get generation for a pokemon ID
  const getGeneration = useCallback((id) => {
    for (const [gen, range] of Object.entries(GENERATIONS)) {
      if (id >= range.start && id <= range.end) {
        return parseInt(gen);
      }
    }
    return 1;
  }, []);
  
  // Get sprite URLs
  const getSprites = useCallback((pokemon) => {
    if (!pokemon) return null;
    return {
      default: pokemon.sprites?.other?.['official-artwork']?.front_default 
        || pokemon.sprites?.front_default,
      shiny: pokemon.sprites?.other?.['official-artwork']?.front_shiny 
        || pokemon.sprites?.front_shiny,
      home: pokemon.sprites?.other?.home?.front_default,
      homeShiny: pokemon.sprites?.other?.home?.front_shiny,
      pixel: pokemon.sprites?.front_default,
      pixelShiny: pokemon.sprites?.front_shiny
    };
  }, []);
  
  return {
    loading,
    error,
    getPokemonList,
    getPokemon,
    getPokemonSpecies,
    getAbility,
    getMove,
    getEvolutionChain,
    getType,
    getLocalizedName,
    getLocalizedFlavorText,
    getLocalizedEffect,
    calculateTypeEffectiveness,
    getGeneration,
    getSprites,
    GENERATIONS,
    TYPE_CHART
  };
}

export default usePokemon;
